import { TagData, TagIconType, TagID, TagType } from 'Types/tag'

export type TagDisplaySource = TagData | TagType | null | undefined

export type ResolvedTagDisplay = {
  id?: TagID
  label: string
  iconType: TagIconType
  iconValue: string
  textColor: string
  watermarkUrl: string
  showBadgeDot: boolean
  isNew: boolean
  isEvent: boolean
}

const EMPTY_TAG_DISPLAY: ResolvedTagDisplay = {
  label: '',
  iconType: 'none',
  iconValue: '',
  textColor: '',
  watermarkUrl: '',
  showBadgeDot: false,
  isNew: false,
  isEvent: false,
}

type LegacyTagFallback = {
  ids?: TagID[]
  aliases: string[]
  iconType?: TagIconType
  iconValue?: string
  textColor?: string
  watermarkUrl?: string
}

const legacyTagFallbackMap: LegacyTagFallback[] = [
  {
    ids: ['Halloween 2025', 'Halloween2025'],
    aliases: ['Halloween 2025', 'Halloween2025'],
    iconType: 'asset',
    iconValue: '/Lottie/Halloween2025tag.json',
    textColor: 'rgba(215,210,52,1)',
    watermarkUrl: '/Halloween2025/Halloween2025Watermark.png',
  },
  {
    ids: ['Christmas 2025', 'Christmas2025'],
    aliases: ['Christmas 2025', 'Christmas2025'],
    iconType: 'asset',
    iconValue: '/Lottie/Christmas2025tag.json',
    textColor: '#D7D234',
    watermarkUrl: '/Christmas2025/Christmas2025Watermark.png',
  },
  {
    ids: ['Valentines2026'],
    aliases: ['Valentines2026'],
    iconType: 'asset',
    iconValue: '/Lottie/Valentines2026tag.json',
    textColor: '#FD7AA1',
    watermarkUrl: '/Valentines2026/Valentines2026Watermark.png',
  },
  {
    ids: ['Under The Mask'],
    aliases: ['Under The Mask'],
    iconType: 'emoji',
    iconValue: '🎭',
    textColor: '#D7D234',
  },
  {
    ids: ['Brewing & Coding'],
    aliases: ['Brewing & Coding'],
    iconType: 'emoji',
    iconValue: '💻',
    textColor: '#34C759',
  },
  {
    ids: ['NewStart'],
    aliases: ['NewStart'],
    iconType: 'emoji',
    iconValue: '🌱',
    textColor: '#72C761',
    watermarkUrl: '/NewStart/NewStartWatermark.png',
  },
]

const normalizeTagMatcher = (value?: string | null) =>
  (value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')

const matchesLegacyTagKey = (candidate: string, keys?: string[]) =>
  Boolean(candidate) &&
  Boolean(keys?.some((key) => normalizeTagMatcher(key) === candidate))

const getTagId = (tag?: TagDisplaySource) => {
  if (!tag) return ''
  return 'tag' in tag ? tag.id || '' : tag.tag_id || ''
}

const getTagNameCandidates = (tag?: TagDisplaySource) => {
  if (!tag) return []

  const candidates = 'tag' in tag ? [tag.tag] : [tag.desc, tag.alias]
  return [...new Set(candidates.map(normalizeTagMatcher).filter(Boolean))]
}

const getLegacyTagFallback = (tag?: TagDisplaySource) => {
  const tagId = normalizeTagMatcher(getTagId(tag))
  if (tagId) {
    const fallbackById = legacyTagFallbackMap.find((item) =>
      matchesLegacyTagKey(tagId, item.ids)
    )
    if (fallbackById) return fallbackById
  }

  const nameCandidates = getTagNameCandidates(tag)
  return legacyTagFallbackMap.find((item) =>
    nameCandidates.some((candidate) =>
      matchesLegacyTagKey(candidate, item.aliases)
    )
  )
}

const inferIconTypeFromValue = (iconValue?: string) => {
  if (!iconValue) return undefined

  if (/^(https?:)?\/\//.test(iconValue) || iconValue.startsWith('/')) {
    return 'asset' as TagIconType
  }

  if (/\.(json|png|jpe?g|gif|webp|svg)$/i.test(iconValue)) {
    return 'asset' as TagIconType
  }

  return 'emoji' as TagIconType
}

const resolveIconFields = (
  tag: NonNullable<TagDisplaySource>,
  legacyFallback?: LegacyTagFallback
) => {
  const apiIconType = tag.icon_type
  const apiIconValue = tag.icon_value || ''

  if (apiIconType === 'none' && !apiIconValue) {
    return {
      iconType: legacyFallback?.iconType || 'none',
      iconValue: legacyFallback?.iconValue || '',
    }
  }

  if (apiIconType && apiIconValue) {
    return {
      iconType: apiIconType as TagIconType,
      iconValue: apiIconValue,
    }
  }

  if (!apiIconType && apiIconValue) {
    return {
      iconType:
        inferIconTypeFromValue(apiIconValue) ||
        legacyFallback?.iconType ||
        'none',
      iconValue: apiIconValue,
    }
  }

  if (apiIconType && !apiIconValue) {
    return {
      iconType: apiIconType as TagIconType,
      iconValue:
        legacyFallback?.iconType === apiIconType
          ? legacyFallback.iconValue || ''
          : '',
    }
  }

  return {
    iconType: legacyFallback?.iconType || 'none',
    iconValue: legacyFallback?.iconValue || '',
  }
}

export const getTagLabel = (tag?: TagDisplaySource) => {
  if (!tag) return ''
  if ('tag' in tag) return tag.tag || ''
  return tag.desc || tag.alias || ''
}

export const resolveTagDisplay = (
  tag?: TagDisplaySource
): ResolvedTagDisplay => {
  if (!tag) return EMPTY_TAG_DISPLAY

  const label = getTagLabel(tag)
  const legacyFallback = getLegacyTagFallback(tag)
  const { iconType, iconValue } = resolveIconFields(tag, legacyFallback)
  const textColor = tag.text_color || legacyFallback?.textColor || ''
  const watermarkUrl = tag.watermark_url || legacyFallback?.watermarkUrl || ''
  const showBadgeDot = Boolean(tag.show_badge_dot)

  if ('tag' in tag) {
    return {
      id: tag.id,
      label,
      iconType,
      iconValue,
      textColor,
      watermarkUrl,
      showBadgeDot,
      isNew: Boolean(tag.isNew),
      isEvent: Boolean(tag.isEvent),
    }
  }

  return {
    id: tag.tag_id,
    label,
    iconType,
    iconValue,
    textColor,
    watermarkUrl,
    showBadgeDot,
    isNew: Boolean(tag.is_new),
    isEvent: Boolean(tag.is_event),
  }
}

export const findTagById = (tagId: TagID, tags?: TagType[]) =>
  tags?.find((tag) => tag.id === tagId)

export const resolveTagDisplayById = (tagId: TagID, tags?: TagType[]) =>
  resolveTagDisplay(findTagById(tagId, tags))

export const resolveTagDisplaysByIds = (tagIds?: TagID[], tags?: TagType[]) =>
  (tagIds || [])
    .map((tagId) => findTagById(tagId, tags))
    .filter((tag): tag is TagType => Boolean(tag))

export const resolveTagWatermark = (tags?: TagDisplaySource[] | null) => {
  for (const tag of tags || []) {
    const watermarkUrl = resolveTagDisplay(tag).watermarkUrl
    if (watermarkUrl) {
      return watermarkUrl
    }
  }
  return ''
}

export const shouldShowTagBadgeDot = (tag?: TagDisplaySource) =>
  resolveTagDisplay(tag).showBadgeDot

export const getBadgeDotTagNames = (tags?: TagDisplaySource[] | null) =>
  (tags || [])
    .filter((tag) => shouldShowTagBadgeDot(tag))
    .map((tag) => resolveTagDisplay(tag).label)
    .filter(Boolean)
